// ChatBot.jsx — agentic chat drawer.
//
// A floating button bottom-right opens a side drawer (full-screen sheet on
// mobile). Streams messages from Claude with tool use, inline approval cards
// for write tools, and a scrollable transcript.

import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Check, Wrench, Trash2, Plus, PanelLeft, Pencil, BookPlus, Maximize2, ExternalLink, ChevronLeft } from "lucide-react";
import { runChatTurn, WRITE_TOOL_NAMES, useChatHistory, formatCostCents, formatTokens } from "../lib/chat";

// Human-readable labels for tool calls
const TOOL_LABELS = {
  search_pantry: "Searching pantry",
  search_recipes: "Searching recipes",
  get_recipe: "Reading recipe",
  list_appliances: "Listing appliances",
  list_beans: "Listing beans",
  get_taste_profile: "Reading taste profile",
  get_recent_brews: "Reading recent brews",
  get_shopping_list: "Reading shopping list",
  add_shopping_item: "Add shopping item",
  mark_recipe_made: "Mark recipe made",
  toggle_favorite_recipe: "Toggle favorite",
  save_recipe: "Save recipe",
};

function describeWrite(name, input) {
  switch (name) {
    case "add_shopping_item":
      return <>Add <strong className="text-ink-700">{input.text}</strong> to shopping list</>;
    case "mark_recipe_made":
      return <>Log this recipe as cooked{input.servings_made ? ` (${input.servings_made} servings)` : ""}</>;
    case "toggle_favorite_recipe":
      return <>Toggle favorite on this recipe</>;
    default:
      return <>{name}</>;
  }
}

export function ChatBot({ store }) {
  const [open, setOpen] = useState(false);
  // History lives at the parent so it survives drawer close/open AND
  // page reloads (via useChatHistory's localStorage backing). The meter
  // listens for chat-tagged AI usage events and accumulates session cost.
  const chat = useChatHistory(store.userId);

  return (
    <>
      {/* Position lifts above the fixed audio player when one is mounted: the
          PlayerBar sets --player-h on the document root while visible. The
          mobile breakpoint stacks above the bottom tab bar via the safe-bottom
          inset on the .md\:hidden nav (z-30) — the chat sits at z-40. */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ bottom: "calc(var(--player-h, 0px) + var(--chat-base, 1.5rem))" }}
        className="fixed right-5 md:right-6 z-40 w-12 h-12 rounded-full bg-pronto-navy text-white shadow-lg hover:bg-pronto-navy-deep transition-[bottom] duration-200 flex items-center justify-center [--chat-base:6rem] md:[--chat-base:1.5rem]"
        aria-label="Open Pronto chat"
      >
        {open ? <X size={20} strokeWidth={2.2} /> : <MessageCircle size={20} strokeWidth={2.2} />}
      </button>

      <ChatDrawer
        open={open}
        onClose={() => setOpen(false)}
        store={store}
        chat={chat}
      />
    </>
  );
}

// Standalone full-page chat — rendered by App at the `#chat` route, either from
// the drawer's "expand" control (same tab) or "open in new tab" (§F1). Owns its
// own history hook so the popped-out tab is a fully independent chat surface
// (it shares the same localStorage-backed conversations, so both stay in sync).
export function ChatFullPage({ store, onExit }) {
  const chat = useChatHistory(store.userId);
  return <ChatDrawer open fullPage onClose={onExit} store={store} chat={chat} />;
}

function ChatDrawer({ open, onClose, store, chat, fullPage = false }) {
  const {
    conversations, activeId, uiMessages: messages, setUiMessages: setMessages,
    apiHistoryRef, meter, newChat, selectChat, deleteChat, renameChat, clear: onClear,
  } = chat;
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null); // { name, input, resolve }
  const [showList, setShowList] = useState(false); // mobile: chat-list overlay open?
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const activeConv = conversations.find(c => c.id === activeId) || null;
  const activeTitle = activeConv && activeConv.title ? activeConv.title : "New chat";

  // Switching chats mid-turn would tangle the in-flight abort controller with a
  // different transcript — block list actions while a turn is streaming.
  const handleSelect = (id) => { if (busy) return; selectChat(id); setShowList(false); };
  const handleNew = () => { if (busy) return; newChat(); setShowList(false); };
  const handleDelete = (id) => {
    if (busy) return;
    const conv = conversations.find(c => c.id === id);
    if (conv && conv.uiMessages.length > 0 && !window.confirm("Delete this conversation?")) return;
    deleteChat(id);
  };
  const handleRename = (id, title) => renameChat(id, title);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Cancel in-flight on unmount/close
  useEffect(() => {
    if (!open && abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setBusy(false);
      setPendingApproval(null);
    }
  }, [open]);

  const requestApproval = useCallback((name, input) => {
    return new Promise((resolve) => {
      setPendingApproval({ name, input, resolve });
    });
  }, []);

  // decision is `false` (deny) or a truthy value (approve). Write cards with
  // editable fields pass an object payload (e.g. { title }) that runChatTurn
  // merges into the tool input before executing.
  const resolveApproval = (decision) => {
    if (!pendingApproval) return;
    pendingApproval.resolve(decision);
    const approved = !!decision;
    setMessages(prev => prev.map(m => m.id === pendingApproval.uiId ? { ...m, decided: approved ? "approved" : "denied" } : m));
    setPendingApproval(null);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (!store.apiKey) {
      setMessages(prev => [
        ...prev,
        { id: crypto.randomUUID(), role: "system", text: "Add your Anthropic API key in Settings to use chat." },
      ]);
      return;
    }
    setInput("");
    setBusy(true);

    const userMsg = { id: crypto.randomUUID(), role: "user", text };
    setMessages(prev => [...prev, userMsg]);
    apiHistoryRef.current.push({ role: "user", content: text });

    // Set up an assistant container we'll mutate as deltas stream
    const assistantId = crypto.randomUUID();
    setMessages(prev => [...prev, { id: assistantId, role: "assistant", text: "", events: [] }]);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await runChatTurn({
        apiKey: store.apiKey,
        messages: apiHistoryRef.current,
        store,
        signal: ac.signal,
        requestApproval: async (name, input) => {
          // Push an approval card into the transcript and pause
          const cardId = crypto.randomUUID();
          setMessages(prev => [
            ...prev,
            { id: cardId, role: "approval", name, input, decided: null },
          ]);
          // Bind the card id to the in-flight request so we can mark decided
          const decision = await new Promise((resolve) => {
            setPendingApproval({ name, input, resolve, uiId: cardId });
          });
          return decision;
        },
        onEvent: (ev) => {
          if (ev.type === "text_delta") {
            setMessages(prev => prev.map(m =>
              m.id === assistantId ? { ...m, text: m.text + ev.text } : m
            ));
          } else if (ev.type === "tool_use_start") {
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, events: [...m.events, { type: "tool", id: ev.id, name: ev.name, status: "running" }] }
                : m
            ));
          } else if (ev.type === "tool_result") {
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              return {
                ...m,
                events: m.events.map(e =>
                  e.id === ev.id ? { ...e, status: ev.is_error ? "error" : "done", result: ev.content } : e
                ),
              };
            }));
            // Open a fresh assistant bubble for the next text round
            // (the next turn starts a new assistant_start)
          } else if (ev.type === "tool_denied") {
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              return {
                ...m,
                events: m.events.map(e => e.id === ev.id ? { ...e, status: "denied" } : e),
              };
            }));
          } else if (ev.type === "assistant_start") {
            // After a tool round, model speaks again — open a new bubble
            setMessages(prev => {
              const last = prev[prev.length - 1];
              // If last is the streaming assistant we already opened and it has no text yet, reuse it
              if (last && last.role === "assistant" && !last.text && !last.events.length) return prev;
              return [...prev, { id: crypto.randomUUID(), role: "assistant", text: "", events: [], _continuation: true }];
            });
          }
        },
      });
    } catch (err) {
      if (err.name !== "AbortError") {
        const msg = err.message || String(err);
        setMessages(prev => [
          ...prev,
          { id: crypto.randomUUID(), role: "system", text: `Error: ${msg}` },
        ]);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={`fixed inset-0 backdrop z-40 ${fullPage ? "hidden" : ""}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={fullPage
              ? "fixed inset-0 z-50 w-full bg-pronto-page flex safe-top safe-bottom overflow-hidden"
              : "fixed inset-y-0 right-0 z-50 w-full md:w-[680px] bg-pronto-page shadow-2xl flex safe-top safe-bottom overflow-hidden"}
            initial={fullPage ? false : { x: "100%" }}
            animate={fullPage ? undefined : { x: 0 }}
            exit={fullPage ? undefined : { x: "100%" }}
            transition={fullPage ? undefined : { type: "spring", damping: 28, stiffness: 280 }}
          >
            <ChatList
              conversations={conversations}
              activeId={activeId}
              show={showList}
              onSelect={handleSelect}
              onNew={handleNew}
              onDelete={handleDelete}
              onRename={handleRename}
              onHide={() => setShowList(false)}
            />

            <div className="flex-1 flex flex-col min-w-0">
            <header className="flex items-center justify-between px-4 py-4 border-b border-parchment-200">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setShowList(true)}
                  className="md:hidden text-ink-400 hover:text-ink-700 p-1.5 -ml-1.5 rounded-lg hover:bg-parchment-200/60 shrink-0"
                  aria-label="Show chats"
                >
                  <PanelLeft size={18} />
                </button>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-medium text-ink-700 truncate">{activeTitle}</h3>
                  <CostMeter meter={meter} />
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!fullPage && (
                  <>
                    <button
                      onClick={() => { window.location.hash = "chat"; }}
                      className="text-ink-400 hover:text-ink-700 p-1.5 rounded-lg hover:bg-parchment-200/60"
                      aria-label="Expand chat to full page"
                      title="Expand to full page"
                    >
                      <Maximize2 size={16} />
                    </button>
                    <button
                      onClick={() => window.open(`${window.location.pathname}#chat`, "_blank", "noopener")}
                      className="text-ink-400 hover:text-ink-700 p-1.5 rounded-lg hover:bg-parchment-200/60"
                      aria-label="Open chat in a new tab"
                      title="Open in new tab"
                    >
                      <ExternalLink size={16} />
                    </button>
                  </>
                )}
                {messages.length > 0 && (
                  <button
                    onClick={() => {
                      if (busy) return;
                      if (window.confirm("Clear this conversation?")) {
                        onClear();
                      }
                    }}
                    disabled={busy}
                    className="text-ink-400 hover:text-pronto-navy disabled:opacity-30 p-1.5 rounded-lg hover:bg-parchment-200/60"
                    aria-label="Clear chat"
                    title="Clear chat"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-ink-400 hover:text-ink-700 p-1.5 rounded-lg hover:bg-parchment-200/60 flex items-center gap-1"
                  aria-label={fullPage ? "Back to app" : "Close chat"}
                  title={fullPage ? "Back to app" : "Close chat"}
                >
                  {fullPage ? <><ChevronLeft size={18} /> <span className="text-sm font-sf hidden sm:inline">Back to app</span></> : <X size={20} />}
                </button>
              </div>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.length === 0 && <ChatEmpty />}
              {messages.map(msg => (
                <ChatMessage key={msg.id} msg={msg} onDecide={resolveApproval} />
              ))}
              {busy && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex items-center gap-2 text-ink-400 text-xs">
                  <Loader2 size={12} className="animate-spin" /> thinking
                </div>
              )}
            </div>

            <footer className="border-t border-parchment-200 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="What's in my pantry? Plan tomorrow's coffee…"
                  rows={1}
                  className="flex-1 resize-none bg-[var(--pg-7)] border border-[var(--p-sep-12)] rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-pronto-navy text-pronto-ink max-h-32"
                />
                <button
                  onClick={send}
                  disabled={busy || !input.trim()}
                  className="w-9 h-9 rounded-xl bg-pronto-navy text-white disabled:opacity-30 hover:bg-pronto-navy-deep transition-colors flex items-center justify-center shrink-0"
                  aria-label="Send"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </footer>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Compact "3m ago" / "2d ago" stamp for the conversation list.
function relativeTime(ts) {
  if (!ts) return "";
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  return `${Math.max(1, Math.floor(day / 30))}mo ago`;
}

// Left rail: the conversation list + "New chat". Always visible on desktop
// (md:flex); on mobile it slides in as an overlay toggled by `show`.
function ChatList({ conversations, activeId, show, onSelect, onNew, onDelete, onRename, onHide }) {
  const sorted = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);
  return (
    <>
      {show && <div className="md:hidden absolute inset-0 z-10 bg-ink-900/25" onClick={onHide} aria-hidden />}
      <aside
        className={`${show ? "flex" : "hidden"} md:flex absolute md:relative inset-y-0 left-0 z-20 w-[78%] max-w-[280px] md:w-[220px] shrink-0 flex-col bg-parchment-100 border-r border-parchment-200 safe-top safe-bottom`}
      >
        <div className="p-3">
          <button
            onClick={onNew}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-pronto-navy text-white text-sm font-medium hover:bg-pronto-navy-deep transition-colors"
          >
            <Plus size={16} /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
          {sorted.length === 0 && (
            <p className="text-xs text-ink-400 text-center px-3 py-6">No conversations yet.</p>
          )}
          {sorted.map(c => (
            <ChatListItem
              key={c.id}
              conv={c}
              active={c.id === activeId}
              onSelect={() => onSelect(c.id)}
              onDelete={() => onDelete(c.id)}
              onRename={onRename}
            />
          ))}
        </div>
      </aside>
    </>
  );
}

function ChatListItem({ conv, active, onSelect, onDelete, onRename }) {
  const title = conv.title || "New chat";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef(null);

  const startEdit = () => { setDraft(conv.title || ""); setEditing(true); };
  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (conv.title || "")) onRename(conv.id, next);
  };

  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  if (editing) {
    return (
      <div className="rounded-lg bg-parchment-200/70 px-2 py-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          maxLength={80}
          placeholder="Chat name"
          className="w-full bg-pronto-page border border-parchment-300 rounded-md px-2 py-1 text-sm text-ink-700 focus:outline-none focus:border-ink-400"
        />
      </div>
    );
  }

  return (
    <div className={`group relative rounded-lg transition-colors ${active ? "bg-parchment-300/70" : "hover:bg-parchment-200/70"}`}>
      <button onClick={onSelect} onDoubleClick={startEdit} className="w-full text-left pl-3 pr-14 py-2 min-w-0 block">
        <div className={`truncate text-sm ${active ? "text-ink-700 font-medium" : "text-ink-600"}`}>{title}</div>
        <div className="text-[10px] text-ink-400 mt-0.5">{relativeTime(conv.updatedAt)}</div>
      </button>
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          onClick={startEdit}
          className="p-1.5 rounded-md text-ink-400 hover:text-ink-700 hover:bg-parchment-100"
          aria-label="Rename chat"
          title="Rename chat"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-md text-ink-400 hover:text-pronto-navy hover:bg-parchment-100"
          aria-label="Delete chat"
          title="Delete chat"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function CostMeter({ meter }) {
  if (!meter || meter.calls === 0) {
    return (
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 mt-0.5">
        pantry · recipes · coffee
      </div>
    );
  }
  const cacheRatio = meter.cacheReadTokens && meter.tokens
    ? Math.round((meter.cacheReadTokens / (meter.tokens + meter.cacheReadTokens)) * 100)
    : 0;
  return (
    <div
      className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-400 mt-0.5 flex items-center gap-2 tabular"
      title={`${meter.calls} call${meter.calls === 1 ? "" : "s"} · ${meter.tokens.toLocaleString()} non-cached tokens · ${meter.cacheReadTokens.toLocaleString()} cache reads`}
    >
      <span className="text-ink-500">{formatTokens(meter.tokens)}</span>
      <span className="text-ink-300">·</span>
      <span className="text-ember-600">{formatCostCents(meter.costCents)}</span>
      {cacheRatio >= 30 && (
        <>
          <span className="text-ink-300">·</span>
          <span className="text-sage-600 normal-case" title="Share of input tokens served from prompt cache">{cacheRatio}% cached</span>
        </>
      )}
    </div>
  );
}

function ChatEmpty() {
  return (
    <div className="text-center py-10 px-2">
      <div className="text-3xl mb-3">🍳</div>
      <h4 className="font-display text-base text-ink-600 mb-1">Ask anything</h4>
      <p className="text-xs text-ink-400 max-w-[14rem] mx-auto">
        I can search your pantry, suggest recipes, plan a brew, or add to your shopping list.
      </p>
      <div className="mt-4 space-y-1.5 text-xs text-ink-500">
        <div className="italic">"What can I make tonight in 30 min?"</div>
        <div className="italic">"How fresh are my beans?"</div>
        <div className="italic">"Add lemons to shopping"</div>
      </div>
    </div>
  );
}

function ChatMessage({ msg, onDecide }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-pronto-accent text-white rounded-[18px] rounded-br-[5px] px-3.5 py-2 text-sm whitespace-pre-wrap">
          {msg.text}
        </div>
      </div>
    );
  }

  if (msg.role === "system") {
    return (
      <div className="text-center text-xs text-ink-400 italic">{msg.text}</div>
    );
  }

  if (msg.role === "approval") {
    return <ApprovalCard msg={msg} onDecide={onDecide} />;
  }

  // assistant
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] space-y-2">
        {msg.events && msg.events.length > 0 && (
          <div className="space-y-1">
            {msg.events.map(ev => <ToolEvent key={ev.id} event={ev} />)}
          </div>
        )}
        {msg.text && (
          <div className="bg-[var(--pg-6)] backdrop-blur-[20px] text-pronto-ink rounded-[18px] rounded-bl-[5px] px-3.5 py-2 text-sm whitespace-pre-wrap">
            {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolEvent({ event }) {
  const label = TOOL_LABELS[event.name] || event.name;
  const isWrite = WRITE_TOOL_NAMES.has(event.name);
  return (
    <div className="flex items-center gap-2 text-[11px] font-mono text-ink-400 px-1">
      {event.status === "running" && <Loader2 size={11} className="animate-spin" />}
      {event.status === "done" && <Check size={11} className="text-sage-500" />}
      {event.status === "error" && <X size={11} className="text-ember-600" />}
      {event.status === "denied" && <X size={11} className="text-ink-400" />}
      {!event.status && <Wrench size={11} />}
      <span>
        {label}
        {event.status === "denied" && " (denied)"}
        {event.status === "error" && " (failed)"}
      </span>
    </div>
  );
}

function ApprovalCard({ msg, onDecide }) {
  if (msg.name === "save_recipe") return <RecipeSaveCard msg={msg} onDecide={onDecide} />;
  const decided = msg.decided;
  return (
    <div className="bg-[rgba(63,74,99,0.06)] border border-[rgba(63,74,99,0.3)] rounded-xl p-3 text-sm">
      <div className="flex items-start gap-2">
        <Wrench size={14} className="text-ember-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ember-600 mb-1">
            confirm action
          </div>
          <div className="text-ink-700 leading-relaxed">
            {describeWrite(msg.name, msg.input)}
          </div>
        </div>
      </div>
      {decided == null ? (
        <div className="flex gap-2 mt-3 ml-6">
          <button
            onClick={() => onDecide(true)}
            className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-pronto-navy text-white hover:bg-pronto-navy-deep font-medium"
          >
            Approve
          </button>
          <button
            onClick={() => onDecide(false)}
            className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-parchment-200 text-ink-600 hover:bg-parchment-300 font-medium"
          >
            Deny
          </button>
        </div>
      ) : (
        <div className="text-[11px] font-mono text-ink-400 mt-2 ml-6">
          {decided === "approved" ? "✓ approved" : "✗ denied"}
        </div>
      )}
    </div>
  );
}

// Keep-or-discard card for a recipe the assistant just created. Shows the
// suggested name (editable) plus a compact preview; on Keep it resolves the
// approval with the possibly-edited title, which runChatTurn merges into the
// save_recipe input before writing to the archive.
function RecipeSaveCard({ msg, onDecide }) {
  const recipe = msg.input || {};
  const decided = msg.decided;
  const [title, setTitle] = useState(recipe.title || "Untitled recipe");

  const ingCount = (recipe.ingredients || []).length;
  const stepCount = (recipe.steps || []).length;
  const totalMin = recipe.total_min || 0;
  const tags = [...(recipe.cuisine || []), ...(recipe.mood_tags || [])].slice(0, 4);

  const meta = [
    ingCount ? `${ingCount} ingredient${ingCount === 1 ? "" : "s"}` : null,
    stepCount ? `${stepCount} step${stepCount === 1 ? "" : "s"}` : null,
    totalMin ? `${totalMin} min` : null,
  ].filter(Boolean);

  const keep = () => onDecide({ title: title.trim() || recipe.title || "Untitled recipe" });

  return (
    <div className="bg-sage-500/5 border border-sage-500/30 rounded-xl p-3 text-sm">
      <div className="flex items-start gap-2">
        <BookPlus size={14} className="text-sage-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-wider text-sage-700 mb-1.5">
            save to recipes
          </div>
          {decided == null ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Recipe name"
              className="w-full bg-pronto-page border border-parchment-300 rounded-lg px-2.5 py-1.5 text-sm font-medium text-ink-700 focus:outline-none focus:border-sage-500"
            />
          ) : (
            <div className="text-ink-700 font-medium leading-snug">{title}</div>
          )}
          {(meta.length > 0 || tags.length > 0) && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-[11px] text-ink-400">
              {meta.length > 0 && <span className="font-mono">{meta.join(" · ")}</span>}
              {tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded-full bg-parchment-200 text-ink-500 lowercase">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {decided == null ? (
        <div className="flex gap-2 mt-3 ml-6">
          <button
            onClick={keep}
            className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-sage-500 text-white hover:bg-sage-700 font-medium"
          >
            Keep
          </button>
          <button
            onClick={() => onDecide(false)}
            className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-parchment-200 text-ink-600 hover:bg-parchment-300 font-medium"
          >
            Discard
          </button>
        </div>
      ) : (
        <div className="text-[11px] font-mono text-ink-400 mt-2 ml-6">
          {decided === "approved" ? "✓ kept" : "✗ discarded"}
        </div>
      )}
    </div>
  );
}
